import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { camelCase } from 'lodash-es';
import type { ReactNode } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { PROJECT_CONST, QUERY_KEY_CONST } from '@/constant';
import { STATUS_COLOR_MAP } from '@/constant/taskConstant';
import { useTypedSearchParams } from '@/hook';
import {
  Assignment,
  KeyboardArrowDown,
  OpenInNew,
  VBaseSelect,
  VButton,
  VDrawer,
  VFlex,
  VStack,
  type VStatusBadgeProps,
  cn,
  useMergedRef,
  useVBaseSelectContext,
  uuid,
} from '@/package';
import { VStatusBadge } from '@/package';
import type { Project, ProjectSearchParams, Task } from '@/type';
import { mapTaskStateToWorkProgress } from '@/util';

import { MyWorkTaskDetail } from '../MyWorkTaskDetail';
import type { WorkerTableProps } from './type';

const MyWorkTaskTable = forwardRef<HTMLDivElement, WorkerTableProps>(
  ({ projectList }, ref) => {
    return (
      <VStack className="w-full">
        <WorkerTaskTableHeader />
        {projectList?.length ? (
          <WorkerTaskTableBody projectList={projectList} ref={ref} />
        ) : (
          <WorkerTaskTableEmpty />
        )}
      </VStack>
    );
  },
);

export default MyWorkTaskTable;

const WorkerTableHedaerCell = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <VFlex items="center" className={cn('px-2', className)}>
      {children}
    </VFlex>
  );
};

const WorkerTaskTableHeader = () => {
  return (
    <>
      <VFlex className="h-9 border-b border-b-gray-300 bg-gray-100 text-body-lg whitespace-nowrap text-gray-500">
        <WorkerTableHedaerCell className="w-[80px]">
          <div>Project ID</div>
        </WorkerTableHedaerCell>

        <WorkerTableHedaerCell className="w-[64px]">
          <div>Task ID</div>
        </WorkerTableHedaerCell>

        <WorkerTableHedaerCell className="w-[114px]">
          <MyWorkStatusFilter />
        </WorkerTableHedaerCell>

        <WorkerTableHedaerCell className="w-[248px]">
          <div>Frame state</div>
        </WorkerTableHedaerCell>

        <WorkerTableHedaerCell className="flex-1">
          <div>Task name</div>
        </WorkerTableHedaerCell>

        <WorkerTableHedaerCell className="w-[224px]">
          <div>Start/Due</div>
        </WorkerTableHedaerCell>

        <WorkerTableHedaerCell className="w-[134px]">
          <div>Competion</div>
        </WorkerTableHedaerCell>

        <WorkerTableHedaerCell className="w-[132px]">
          <div>Last worked frame</div>
        </WorkerTableHedaerCell>
      </VFlex>
    </>
  );
};

const WorkerTableRow = ({
  project,
  selectTask,
  selectedTask,
}: {
  project: Project;
  selectTask: (task?: Task) => void;
  selectedTask: Nullish<Task>;
}) => {
  return (
    <>
      <VFlex className={cn('w-full bg-gray-100')}>
        <VStack className="h-full w-[80px]">
          <VFlex className="h-[70px] w-full" items="center">
            <WorkerTableRowCell className="w-[80px]">
              {project.id}
            </WorkerTableRowCell>
          </VFlex>
        </VStack>
        <VStack className="flex-1 overflow-x-hidden">
          {project.task?.map(task => (
            <WorkerTableTaskRow
              selectTask={selectTask}
              selectedTask={selectedTask}
              task={task}
              key={task.id}
            />
          ))}
        </VStack>
      </VFlex>
    </>
  );
};

const WorkerTableRowCell = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <VFlex className={cn('p-2', className)} items="center">
      {children}
    </VFlex>
  );
};

const WorkerTableTaskRow = ({
  task,
  selectTask,
  selectedTask,
}: {
  task: Task;
  selectTask: (task?: Task) => void;
  selectedTask: Nullish<Task>;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const userDoneFrameCount = task.userTask?.[0].doneFrameCount ?? 0;
  const userTotalFrameCount = task.userTask?.[0].totalFrameCount ?? 0;

  const latestSeq = task.userTask?.[0].lastFrameSeq;

  const taskState = mapTaskStateToWorkProgress(task);

  const onLatestSeqClick = () => {
    queryClient.removeQueries({
      queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail(),
    });
    queryClient.removeQueries({
      queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail(),
    });
    navigate(`/labeling/${task.projectID}/${task.id}/${latestSeq}?myWork=true`);
  };

  return (
    <VFlex
      className="h-[70px] w-full items-center overflow-hidden border-b border-b-gray-200 aria-pressed:bg-skyBlue-200"
      role="button"
      aria-pressed={task.id === selectedTask?.id}
      onClick={() => selectTask(task)}
    >
      <WorkerTableRowCell className="w-16">{task.id}</WorkerTableRowCell>

      <WorkerTableRowCell className="w-[114px]">
        <VStatusBadge
          variant={
            camelCase(taskState.toLowerCase()) as VStatusBadgeProps['variant']
          }
        >
          {taskState}
        </VStatusBadge>
      </WorkerTableRowCell>

      <WorkerTableRowCell className="w-[248px]">
        <VFlex items="center" gap={0.5}>
          <div>
            <span
              style={{
                color: STATUS_COLOR_MAP[taskState],
              }}
              className="font-bold"
            >
              {userDoneFrameCount ?? 0}{' '}
            </span>
            /{userTotalFrameCount ?? 0}
          </div>
          <div className="h-4 w-px bg-gray-300" />
          <span
            style={{
              color: STATUS_COLOR_MAP[taskState],
            }}
            className="font-bold"
          >
            {Math.round(
              (userDoneFrameCount / (userTotalFrameCount || 1)) * 100,
            )}
            %
          </span>
        </VFlex>
      </WorkerTableRowCell>

      <WorkerTableRowCell className="min-w-0 flex-1">
        <VStack className="w-full">
          <span className="inline-block w-full min-w-0 truncate text-body-xl">
            {task.taskName || task.orgPath}
          </span>
          <span className="inline-block w-full min-w-0 truncate text-body-lg text-gray-400">
            {[task.basePath, task.orgPath].join('/')}
          </span>
        </VStack>
      </WorkerTableRowCell>

      <WorkerTableRowCell className="w-[224px]">
        <span>
          {dayjs(task.startDate).format('YYYY-MM-DD')} ~{' '}
          {dayjs(task.endDate).format('YYYY-MM-DD')}
        </span>
      </WorkerTableRowCell>

      <WorkerTableRowCell className="w-[134px]">-</WorkerTableRowCell>

      <WorkerTableRowCell className="w-[132px]">
        {!!latestSeq && (
          <VFlex
            justify="center"
            items="center"
            gap={0.5}
            className="w-full"
            onClick={e => {
              e.stopPropagation();
              onLatestSeqClick();
            }}
          >
            <VButton size="sm">
              <OpenInNew className="h-4 w-4" />
              {latestSeq}
            </VButton>
          </VFlex>
        )}
      </WorkerTableRowCell>
    </VFlex>
  );
};

const WorkerTaskTableBody = forwardRef<
  HTMLDivElement,
  { projectList: Project[] }
>(({ projectList }, ref) => {
  const [selectedTask, setSelectedTask] = useState<Nullish<Task>>();
  const [_, setSearchParams] = useSearchParams();

  const selectTask = (task?: Task) => {
    setSelectedTask(task);
  };

  useEffect(() => {
    const selectedTaskID =
      new URLSearchParams(window.location.search).get('selectedTaskId') ?? null;

    if (!selectedTaskID) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedTask({ id: +selectedTaskID } as unknown as Task);
  }, []);

  return (
    <>
      <VStack className="w-full">
        {projectList.map(project => (
          <WorkerTableRow
            key={project.id}
            project={project}
            selectTask={selectTask}
            selectedTask={selectedTask}
          />
        ))}
        <div
          className="h-0.25 w-full"
          id="my-task-load-more-trigger"
          ref={ref}
        />
      </VStack>

      <VDrawer
        visible={!!selectedTask}
        onUpdateVisible={visible => !visible && setSelectedTask(null)}
        position="right"
        onShow={() => {
          setSearchParams(
            prev => {
              prev.set('frameLimit', '20');
              prev.set('frameOffset', '0');

              if (selectedTask) {
                prev.set('selectedTaskId', `${selectedTask.id}`);
              }

              return prev;
            },
            { replace: true },
          );
        }}
        onHidden={() => {
          setSearchParams(
            prev => {
              prev.delete('frameLimit');
              prev.delete('frameOffset');
              prev.delete('frameKeyword');
              prev.delete('frameWorkerId');
              prev.delete('frameReviewerId');
              prev.delete('frameWorkState');
              prev.delete('selectedTaskId');
              return prev;
            },
            { replace: true },
          );
        }}
      >
        {selectedTask && (
          <MyWorkTaskDetail
            task={selectedTask}
            deselectTask={() => selectTask()}
          />
        )}
      </VDrawer>
    </>
  );
});

const WorkerTaskTableEmpty = () => {
  const { t } = useTranslation();
  return (
    <VStack className="mt-16 w-full gap-1 py-10" items="center">
      <Assignment className="size-12 text-gray-300" />
      <div className="text-body-xl text-gray-300">
        {t('myWork.emptyProjectList.phanerogam')}
      </div>
    </VStack>
  );
};

const MyWorkStatusFilter = () => {
  const { getAll, remove, append, flush } =
    useTypedSearchParams<ProjectSearchParams>();

  const statusFilterItemList = Object.values(PROJECT_CONST.PROJECT_STATE).map(
    state => {
      const isSelected = getAll('projectState').includes(state);

      return { value: state, label: state, isSelected };
    },
  );

  const isAllSelected = statusFilterItemList.every(item => item.isSelected);
  const isAllUnselected = statusFilterItemList.every(item => !item.isSelected);
  const allUUID = uuid();
  const expandedFilterItemList = [
    {
      value: allUUID,
      label: 'All',
      isSelected: isAllSelected || isAllUnselected,
    },
    ...statusFilterItemList,
  ];

  const orgValue = statusFilterItemList
    .filter(item => item.isSelected)
    .map(item => item.value);

  const value =
    isAllSelected || isAllUnselected
      ? [allUUID, ...(orgValue ?? [])]
      : orgValue;

  return (
    <VFlex gap={0.5} items="center">
      <div>Status</div>

      <VBaseSelect
        itemList={expandedFilterItemList}
        multiple
        value={value}
        onItemClick={e => {
          remove('projectState');
          const targetItem = expandedFilterItemList.find(
            item => item.value === e.target.item.value,
          );

          if (targetItem?.value === allUUID) {
            flush();
            return;
          }

          const nextItemList = statusFilterItemList.map(item => ({
            ...item,
            isSelected:
              item.value === targetItem?.value
                ? !targetItem.isSelected
                : item.isSelected,
          }));

          nextItemList.forEach(item => {
            if (!item.isSelected) return;
            append('projectState', item.value);
          });

          flush();
        }}
      >
        <FilterPopup />
      </VBaseSelect>
    </VFlex>
  );
};

const FilterPopup = forwardRef((_, ref) => {
  const { $displayValue } = useVBaseSelectContext();
  const refs = useMergedRef($displayValue, ref);

  return <KeyboardArrowDown className="size-4" ref={refs} />;
});
